import React, { useState } from 'react';
import { billingService } from '../../api/services/billing.service';
import { InvoiceDTO, PaymentMethod, paymentMethodLabels, formatMoney } from '../../types/billing';
import ConfirmDialog from '../../components/Common/ConfirmDialog';
import { isUsageInvoice } from './closeBooksRules';

interface Props {
  invoice: InvoiceDTO;
  onClose: () => void;
  onDone: (message: string) => void;
}

const inputCls =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:ring-indigo-500 focus:border-indigo-500';

/**
 * A payment against one invoice. `addCredits` is sent false on purpose: the server
 * credits an invoice payment itself (a usage invoice's subtotal, a purchase's
 * credits), and the Payments page's default of true must not ride along.
 */
const SettleInvoiceModal: React.FC<Props> = ({ invoice, onClose, onDone }) => {
  const [amount, setAmount] = useState(String(invoice.balanceDue ?? ''));
  const [method, setMethod] = useState<PaymentMethod>(PaymentMethod.BANK_TRANSFER);
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const value = Number(amount);
  const valid = amount !== '' && value > 0;
  const full = valid && value >= (invoice.balanceDue ?? 0);
  const money = (v?: number) => formatMoney(v ?? 0, invoice.currency || 'INR');

  const record = async () => {
    setBusy(true);
    setError(null);
    const r = await billingService.recordPayment({
      organizationId: invoice.organizationId,
      invoiceId: invoice.id,
      amount: value,
      paymentMethod: method,
      currency: invoice.currency,
      transactionId: reference || undefined,
      notes: notes || undefined,
      addCredits: false,
    });
    setBusy(false);
    setConfirming(false);
    if (!r.success) {
      setError(r.error || 'Failed to record payment');
      return;
    }
    onDone(`${money(value)} recorded against ${invoice.invoiceNumber}${full ? ': paid in full' : ''}.`);
  };

  const effect = isUsageInvoice(invoice)
    ? full
      ? `Paid in full, the account is credited ${money(invoice.subtotal)} (the subtotal; GST is tax, not credit), settling the usage.`
      : 'A part payment credits nothing yet: the settlement credit is made when the invoice is paid in full.'
    : 'The invoice is marked paid when the full total is in; any credits it carries are added then.';

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto mx-4">
          <h2 className="text-xl font-semibold mb-1 dark:text-white">Record payment</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {invoice.invoiceNumber} · {invoice.organizationName || invoice.organizationId} · due {money(invoice.balanceDue)}
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (valid) setConfirming(true);
            }}
            className="space-y-4"
          >
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Amount *</label>
                <input type="number" step="0.01" min="0.01" required value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Method *</label>
                <select value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)} className={inputCls}>
                  {Object.entries(paymentMethodLabels).map(([v, label]) => (
                    <option key={v} value={v}>{label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Bank / UTR reference</label>
              <input type="text" value={reference} onChange={(e) => setReference(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
              <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">{effect}</p>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button type="submit" disabled={!valid} className="tl-btn tl-btn-primary disabled:opacity-50">
                Record
              </button>
            </div>
          </form>
        </div>
      </div>
      <ConfirmDialog
        isOpen={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={record}
        isLoading={busy}
        variant="warning"
        title="Record this payment?"
        message={`${money(value)} by ${paymentMethodLabels[method]} against ${invoice.invoiceNumber}. ${effect} A recorded payment cannot be undone from the portal.`}
        confirmText="Record payment"
      />
    </>
  );
};

export default SettleInvoiceModal;
